@if (isImage() && mediaUrl()) {
  <div class="cmp-card__media" [style.background-image]="mediaUrl()" [style.background-position]="imagePosition()"></div>
  <div class="cmp-card__scrim" aria-hidden="true"></div>
}

<div class="cmp-card__body">
  @switch (variant()) {
    @case ('content') {
      @if (headline()) {
        <!-- Hero (image/filled): icon top-left, eyebrow + display headline + action bottom. -->
        <div class="cc cc--hero" [class.cc--image]="isImage()" [class.cc--filled]="appearance() === 'filled'">
          @if (icon()) {
            @if (isImage()) {
              <div class="cc__media-icon" [innerHTML]="iconSvg(icon(), 48)" aria-hidden="true"></div>
            } @else {
              <div class="cc__icon" [class.cc__icon--neutral]="iconStyle() === 'neutral'" [innerHTML]="iconSvg(icon(), 24)" aria-hidden="true"></div>
            }
          }
          <div class="cc__img-foot">
            <div class="cc__hero-text">
              @if (title()) { <p class="cc__eyebrow">{{ title() }}</p> }
              <ng-container [ngTemplateOutlet]="headlineTpl" />
              @if (description()) { <p class="cc__desc">{{ description() }}</p> }
            </div>
            @if (actionLabel()) {
              <span class="cc__button cc__button--solid">{{ actionLabel() }}</span>
            } @else if (showArrow()) {
              <span class="cc__arrow" [innerHTML]="iconSvg('arrow-up-right', 24)" aria-hidden="true"></span>
            }
          </div>
        </div>
      } @else if (isImage()) {
        <div class="cc cc--image">
          <div class="cc__img-top">
            @if (icon()) {
              <div class="cc__media-icon" [innerHTML]="iconSvg(icon(), 48)" aria-hidden="true"></div>
            }
            @if (category()) { <span class="cc__chip cc__chip--glass">{{ category() }}</span> }
          </div>
          <div class="cc__img-foot">
            @if (title() || subtitle() || description()) {
              <div class="cc__hero-text">
                @if (title() || subtitle()) {
                  <div class="cc__heading">
                    @if (title()) { <ng-container [ngTemplateOutlet]="titleTpl" /> }
                    @if (subtitle()) { <p class="cc__subtitle">{{ subtitle() }}</p> }
                  </div>
                }
                @if (description()) { <p class="cc__desc">{{ description() }}</p> }
              </div>
            }
            @if (actionLabel()) {
              <span class="cc__button cc__button--solid">{{ actionLabel() }}</span>
            } @else if (showArrow()) {
              <span class="cc__arrow" [innerHTML]="iconSvg('arrow-up-right', 24)" aria-hidden="true"></span>
            }
          </div>
        </div>
      } @else if (appearance() === 'gradient') {
        <div class="cc" [class.cc--filled]="appearance() === 'filled'">
          <div class="cc__top" [class.cc__top--label]="categoryStyle() === 'label'">
            @if (icon()) {
              <div class="cc__icon" [class.cc__icon--neutral]="iconStyle() === 'neutral'" [innerHTML]="iconSvg(icon(), 24)" aria-hidden="true"></div>
            }
            @if (categoryStyle() === 'label' && category()) {
              <span class="cc__category-label">{{ category() }}</span>
            }
            <div class="cc__top-end">
              @if (categoryStyle() === 'chip' && category()) { <span class="cc__chip">{{ category() }}</span> }
              @if (showArrow()) {
                <span class="cc__arrow" [innerHTML]="iconSvg('arrow-up-right', 24)" aria-hidden="true"></span>
              }
            </div>
          </div>
          <div class="cc__text">
            @if (title() || subtitle()) {
              <div class="cc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="titleTpl" /> }
                @if (subtitle()) { <p class="cc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="cc__desc">{{ description() }}</p> }
          </div>
          @if (actionLabel()) { <span class="cc__button">{{ actionLabel() }}</span> }
        </div>
      } @else {
        <!-- With-icon content card (Figma 59444:2263): a full-width light-blue
             icon banner over a gray category eyebrow + title + body, with the
             text distributed top/bottom. Default for outlined/filled content. -->
        <div class="cc cc--with-icon" [class.cc--filled]="appearance() === 'filled'">
          <div class="cc__banner">
            @if (icon()) {
              <span class="cc__banner-icon" [innerHTML]="iconSvg(icon(), 48, 1.5)" aria-hidden="true"></span>
            }
          </div>
          <div class="cc__container">
            <div class="cc__title-group">
              @if (category()) { <p class="cc__category-eyebrow">{{ category() }}</p> }
              @if (title()) { <ng-container [ngTemplateOutlet]="titleTpl" /> }
            </div>
            @if (description()) { <p class="cc__desc">{{ description() }}</p> }
          </div>
        </div>
      }
    }

    @case ('action') {
      @if (orientation() === 'vertical') {
        <div class="ac ac--vertical" [class.ac--no-desc]="!description()">
          @if (icon()) {
            <div class="ac__badge" [innerHTML]="iconSvg(icon(), 24, 1.5)" aria-hidden="true"></div>
          }
          <div class="ac__text">
            <ng-container [ngTemplateOutlet]="labelTpl" />
            @if (description()) { <span class="ac__desc">{{ description() }}</span> }
          </div>
        </div>
      } @else {
        <div class="ac" [class.ac--no-desc]="!description()">
          @if (icon()) {
            <div class="ac__icon" [innerHTML]="iconSvg(icon(), 32)" aria-hidden="true"></div>
          }
          <div class="ac__text">
            <ng-container [ngTemplateOutlet]="labelTpl" />
            @if (description()) { <span class="ac__desc">{{ description() }}</span> }
          </div>
        </div>
      }
    }

    @case ('metric') {
      <div class="mc" [class.mc--current]="current()">
        <span class="mc__value">{{ value() }}</span>
        @if (label()) { <span class="mc__label">{{ label() }}</span> }
      </div>
    }

    @case ('basic') {
      @if (size() === 'xs') {
        <!-- xs base card (Figma 243:3264): compact horizontal row — icon · title · arrow. -->
        <div class="bc bc--row">
          @if (icon()) {
            <div class="bc__icon" [innerHTML]="iconSvg(icon(), 24, 1.5)" aria-hidden="true"></div>
          }
          <div class="bc__row-text">
            @if (title()) { <ng-container [ngTemplateOutlet]="bcTitleTpl" /> }
          </div>
          @if (showArrow()) {
            <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', 20)" aria-hidden="true"></span>
          }
        </div>
      } @else if (size() === 'lg') {
        <!-- lg base card (Figma 243:3205): text + optional chip, button bottom-right. -->
        <div class="bc bc--promo" [class.bc--image]="isImage()">
          <div class="bc__text">
            @if (label()) { <p class="bc__eyebrow">{{ label() }}</p> }
            @if (title() || subtitle()) {
              <div class="bc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="bcTitleTpl" /> }
                @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="bc__desc">{{ description() }}</p> }
            @if (category()) { <span class="bc__chip">{{ category() }}</span> }
          </div>
          @if (actionLabel()) { <span class="bc__button bc__button--end">{{ actionLabel() }}</span> }
        </div>
      } @else if (isImage()) {
        <!-- md image base card (Figma 59442:12042): text over the image, arrow bottom-right. -->
        <div class="bc bc--image">
          <div class="bc__text">
            @if (title() || subtitle()) {
              <div class="bc__heading">
                @if (title()) { <ng-container [ngTemplateOutlet]="bcTitleTpl" /> }
                @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
              </div>
            }
            @if (description()) { <p class="bc__desc">{{ description() }}</p> }
          </div>
          @if (showArrow()) {
            <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', basicArrowSize())" aria-hidden="true"></span>
          }
        </div>
      } @else {
        <!-- md / sm base card (Figma 243:3174, 243:3179, 947:8067):
             icon + arrow row, then eyebrow / title / subtitle / description / button. -->
        <div class="bc">
          <div class="bc__top">
            @if (icon()) {
              @if (appearance() === 'gradient') {
                <div class="bc__icon bc__icon--badge" [innerHTML]="iconSvg(icon(), 32)" aria-hidden="true"></div>
              } @else {
                <div class="bc__icon" [innerHTML]="iconSvg(icon(), basicIconSize(), 1.5)" aria-hidden="true"></div>
              }
            }
            <div class="bc__top-end">
              @if (category()) { <span class="bc__chip">{{ category() }}</span> }
              @if (showArrow()) {
                <span class="bc__arrow" [innerHTML]="iconSvg('arrow-up-right', basicArrowSize())" aria-hidden="true"></span>
              }
            </div>
          </div>
          <div class="bc__body">
            <div class="bc__text">
              @if (label()) { <p class="bc__eyebrow">{{ label() }}</p> }
              @if (title() || subtitle()) {
                <div class="bc__heading">
                  @if (title()) { <ng-container [ngTemplateOutlet]="bcTitleTpl" /> }
                  @if (subtitle()) { <p class="bc__subtitle">{{ subtitle() }}</p> }
                </div>
              }
              @if (description()) { <p class="bc__desc">{{ description() }}</p> }
            </div>
            @if (actionLabel()) { <span class="bc__button">{{ actionLabel() }}</span> }
          </div>
        </div>
      }
    }

    @default {
      <ng-content />
    }
  }
</div>

<!-- Heading fragments: render a real <h2>–<h6> when `headingLevel` is set, else the
     original <p>/<span>. Shared across layouts so semantics stay consistent and the
     cc__title / cc__headline / ac__label styling is unchanged. -->
<ng-template #titleTpl>
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 class="cc__title">{{ title() }}</h2> }
    @case (3) { <h3 class="cc__title">{{ title() }}</h3> }
    @case (4) { <h4 class="cc__title">{{ title() }}</h4> }
    @case (5) { <h5 class="cc__title">{{ title() }}</h5> }
    @case (6) { <h6 class="cc__title">{{ title() }}</h6> }
    @default { <p class="cc__title">{{ title() }}</p> }
  }
</ng-template>

<ng-template #headlineTpl>
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 class="cc__headline">{{ headline() }}</h2> }
    @case (3) { <h3 class="cc__headline">{{ headline() }}</h3> }
    @case (4) { <h4 class="cc__headline">{{ headline() }}</h4> }
    @case (5) { <h5 class="cc__headline">{{ headline() }}</h5> }
    @case (6) { <h6 class="cc__headline">{{ headline() }}</h6> }
    @default { <p class="cc__headline">{{ headline() }}</p> }
  }
</ng-template>

<ng-template #labelTpl>
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 class="ac__label">{{ label() }}</h2> }
    @case (3) { <h3 class="ac__label">{{ label() }}</h3> }
    @case (4) { <h4 class="ac__label">{{ label() }}</h4> }
    @case (5) { <h5 class="ac__label">{{ label() }}</h5> }
    @case (6) { <h6 class="ac__label">{{ label() }}</h6> }
    @default { <span class="ac__label">{{ label() }}</span> }
  }
</ng-template>

<ng-template #bcTitleTpl>
  @switch (effectiveHeadingLevel()) {
    @case (2) { <h2 class="bc__title">{{ title() }}</h2> }
    @case (3) { <h3 class="bc__title">{{ title() }}</h3> }
    @case (4) { <h4 class="bc__title">{{ title() }}</h4> }
    @case (5) { <h5 class="bc__title">{{ title() }}</h5> }
    @case (6) { <h6 class="bc__title">{{ title() }}</h6> }
    @default { <p class="bc__title">{{ title() }}</p> }
  }
</ng-template>
